import {
  Component,
  computed, effect,
  forwardRef,
  inject,
  Injector,
  Input,
  signal,
  ViewChild,
  ViewChildren,
  WritableSignal
} from '@angular/core';
import { PrivilegeHierarchyModel, PrivilegeTreeNode } from '@components/privilege-hierarchy-control/privilege-hierarchy.model';
import { CdkTree, CdkTreeNode, NestedTreeControl } from '@angular/cdk/tree';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PrivilegeHierarchy, PrivilegeNode } from '@artemis-software/wr-api';
import { Tracked } from '@/utils/signals/tracked';
import PrivilegeEnum = PrivilegeNode.PrivilegeEnum;


@Component({
  standalone: false,
  selector: 'wr-privilege-hierarchy-control',
  templateUrl: './privilege-hierarchy-control.component.html',
  styleUrls: ['./privilege-hierarchy-control.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PrivilegeHierarchyControlComponent),
      multi: true,
    },
  ],
})
export class PrivilegeHierarchyControlComponent implements ControlValueAccessor {
  @ViewChild('tree', { static: false })
  @Tracked()
  tree!: CdkTree<PrivilegeTreeNode>;

  @ViewChildren(CdkTreeNode) treeNodesRef!: CdkTreeNode<PrivilegeTreeNode>[];

  treeControl = new NestedTreeControl<PrivilegeTreeNode>(node => node.children);

  @Input()
  @Tracked()
  privilegeHierarchy!: PrivilegeHierarchy;

  readonly rootNodes = computed(() => this.privilegeHierarchy.children as PrivilegeTreeNode[]);

  private _onChange?: (_: any) => void;
  private _onTouched?: () => void;

  @Input()
  @Tracked()
  allowedPrivileges!: PrivilegeEnum[];

  readonly selection: WritableSignal<PrivilegeNode.PrivilegeEnum[]> = signal([]);

  readonly disabled = signal(false);

  model = new PrivilegeHierarchyModel(
    computed(() => this.tree),
    computed(() => this.treeNodesRef),
    this.rootNodes,
    this.selection,
    computed(() => this.allowedPrivileges),
  );

  constructor() {
    this.model.valueChanged.subscribe(() => {
      const value = this.selection();

      this._onChange?.(value);
      this._onTouched?.();
    });
  }

  writeValue(obj: any): void {
    this.selection.set(obj);
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  onCheckboxClick(node: PrivilegeTreeNode, event: MouseEvent) {
    event.stopPropagation();
  }

  getNodePadding(level: number) {
    return `${level * 0.75}rem`;
  }

  onNodeClicked(node: PrivilegeTreeNode, event: MouseEvent) {
    if (this.disabled())
      return;

    event.stopPropagation();
    this.model.toggleSelected(node);
  }
}
